import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth/AuthProvider";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus } from "lucide-react";

const EMPTY = { id: null, email: "", name: "", role: "viewer", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // EMPTY | {...} | null
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (form) => {
    try {
      if (form.id) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await api.patch(`/users/${form.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", {
          email: form.email.trim().toLowerCase(),
          name: form.name,
          role: form.role,
          password: form.password,
        });
        toast.success("User created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Admin
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">
            Users
          </h1>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="users-add-button"
          className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
        >
          <UserPlus size={14} className="mr-2" /> New user
        </Button>
      </div>

      {error && (
        <div
          data-testid="users-error"
          className="mb-6 text-sm text-red-400 border-l-2 border-red-500 pl-3"
        >
          {error}
        </div>
      )}

      <div className="border border-zinc-800" data-testid="users-table-wrap">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Email</th>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Role</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {users.map((u) => (
              <tr
                key={u.id}
                data-testid={`users-row-${u.email}`}
                className="hover:bg-zinc-900/40"
              >
                <td className="p-3 font-mono text-zinc-200">{u.email}</td>
                <td className="p-3 text-zinc-300">{u.name}</td>
                <td className="p-3">
                  <span
                    className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${
                      u.role === "admin"
                        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                        : "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-zinc-100"
                    onClick={() => setEditing({ ...u, password: "" })}
                    data-testid={`users-edit-${u.email}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-red-400"
                    onClick={() => setConfirmDelete(u)}
                    disabled={u.id === me?.id}
                    data-testid={`users-delete-${u.email}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserDialog
          form={editing}
          setForm={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
      {confirmDelete && (
        <AlertDialog open={true} onOpenChange={() => setConfirmDelete(null)}>
          <AlertDialogContent
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
            data-testid="users-confirm-delete-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {confirmDelete.email}?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border-0"
                data-testid="users-confirm-delete-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => remove(confirmDelete)}
                className="rounded-sm bg-red-500 hover:bg-red-400 text-zinc-950 font-bold"
                data-testid="users-confirm-delete-confirm"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </AppShell>
  );
}

function UserDialog({ form, setForm, onSave, onClose }) {
  const isNew = !form.id;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100"
        data-testid="users-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {isNew ? "New user" : `Edit ${form.email}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            User form with email, name, role, and password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isNew && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Email
              </Label>
              <Input
                data-testid="users-dialog-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
              />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Name
            </Label>
            <Input
              data-testid="users-dialog-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Role
            </Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
                data-testid="users-dialog-role"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="viewer">viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Password {isNew ? "" : "(leave blank to keep)"}
            </Label>
            <Input
              data-testid="users-dialog-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
              placeholder={isNew ? "min 8 chars" : ""}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="users-dialog-cancel"
            className="text-zinc-400"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(form)}
            data-testid="users-dialog-save"
            className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
